// gradingLogic.tsx
import { gradingRules, SectionRules, QuestionRule } from "./gradingRules";

export type ResponseValue = string | string[];
export type ResponseRecord = Record<string, ResponseValue>;

export type GradeResult = {
  sectionGrade: string;
  explanation: string;
};

// Helper to find top grade
const getTopGrade = (scores: Record<string, number>): string => {
  let top = "";
  let max = -Infinity;
  for (const [grade, score] of Object.entries(scores)) {
    if (score > max) {
      top = grade;
      max = score;
    }
  }
  return top;
};

// Explanations
const explanationMaps: Record<string, Record<string, string>> = {
  Awareness: {
    Exploring: "You are at the beginning of an exciting journey. Keep your curiosity alive!",
    Helping: "Your heart for service will inspire meaningful impact.",
    Reflective: "Your thoughtful approach will strengthen your mission path.",
    Questioning: "It’s okay to have doubts; seek answers and clarity.",
    Neutral: "Take small steps to discover what missions mean to you.",
  },
  Knowledge: {
    "Beginning Learner": "Keep learning and exploring; growth takes time.",
    "Enthusiastic Volunteer": "Your willingness to help makes a difference!",
    "Committed Planner": "Your readiness sets you on a strong mission path.",
    "Focused Practitioner": "Your balanced approach will maximize impact.",
    "Experienced Seeker": "You are on the right track to becoming a mission expert.",
  },
  Mindset: {
    Cautious: "Growth happens with experience; stay open and keep learning.",
    Determined: "Your resilience will help you overcome obstacles.",
    Adaptable: "Your readiness to embrace new challenges is a great asset.",
    Faithful: "Your spiritual strength will sustain your mission journey.",
    Empowered: "You are well-equipped to make a lasting impact.",
  },
  Vision: {
    "Beginning Dreamer": "Your vision will develop in time; keep nurturing it.",
    "Collaborative Worker": "Your approach will build trusted relationships.",
    "Cautious Planner": "Awareness of risks prepares you well.",
    "Balanced Seeker": "Pursuing harmony is key to sustainable missions.",
    "Growing Leader": "Your ambition will drive continued impact and growth.",
  },
};

// Main grading function
export const gradeSection = (
  sectionName: keyof typeof gradingRules,
  responses: ResponseRecord
): GradeResult => {
  const sectionRules: SectionRules = gradingRules[sectionName];
  const scores: Record<string, number> = {};

  for (const [question, answerValue] of Object.entries(responses)) {
    const rule = sectionRules[question as keyof SectionRules] as QuestionRule | undefined;
    if (!rule) continue;

    const answers = Array.isArray(answerValue) ? answerValue : [answerValue];
    for (const ans of answers) {
      const weight = rule.options[ans];
      if (!weight) continue;
      for (const [grade, value] of Object.entries(weight)) {
        scores[grade] = (scores[grade] || 0) + value;
      }
    }
  }

  const sectionGrade = getTopGrade(scores);
  const explanationMap = explanationMaps[sectionName] || {};
  const explanation =
    explanationMap[sectionGrade] || "Keep developing your strengths and balance across areas.";

  return {
    sectionGrade,
    explanation: `${explanation}`,
  };
};

// Wrappers
export const gradeAwareness = (r: ResponseRecord): GradeResult =>
  gradeSection("Awareness", r);
export const gradeKnowledge = (r: ResponseRecord): GradeResult =>
  gradeSection("Knowledge", r);
export const gradeMindset = (r: ResponseRecord): GradeResult =>
  gradeSection("Mindset", r);
export const gradeVision = (r: ResponseRecord): GradeResult =>
  gradeSection("Vision", r);
